import React from "react";
import { BrPageContext } from "@bloomreach/react-sdk";
import { Desktop, Mobile, IsMobileContent } from "../../../media-queries/MediaQueries";
import useViewMoreToggle from "../../../hooks/useViewMoreToggle";
import Description from "../../Global/Description";
import AddProduct from "../../Global/AddProduct/AddProduct";
import Image from "../../Global/Image/Image";
import Title from "../../Global/Title/Title";
import { HEADING_LEVELS } from "../../Global/GlobalConstants";
import CtaLink from "../../Global/CtaLink";
import { DISPLAYMODE } from "../SearchToolbar/DisplayMode";
import { getSustainableLabels, getSustainableFeaturesList } from "../../Global/SustainableFeatures";
import { getPageTitle } from "../../../helpers/GlobalHelpers";
import SearchCard from "./SearchCard";
import "./ProductCard.scss";

function ProductCard({ 
  product = {}, 
  productImageHeight, 
  productImageWidth, 
  displayMode, 
  externalModalDiv = null, 
  positionModal,
  // Optional props for SavedProducts and other custom use cases
  isDiscontinued = false,
  discontinuedLabel = "",
  discontinuedMessage = "",
  renderAdditionalInfo = null,
  onCtaClick = null,
  customAddProductAnalytics = null,
}) {
  const NUMFEATURESDISPLAYED = 3;
  const page = React.useContext(BrPageContext);
  const isMobile = IsMobileContent();

  // Global Properties
  const {
    mlViewAllProducts = "",
    viewDetailsLabel = "",
    sustainableFeaturesLabel = "",
    productFeaturesLabel = "",
    viewMoreLabel = "",
    viewLessLabel = "view less",
    keyFeaturesLabel = "",
    moreColorsPrintsAvailableLabel = "",
    productCollectionLabel = "",
    addYourBrandLabel = "",
    newProductLabel = "",
  } = window.globalProperties;
  const sustainableLabels = getSustainableLabels();

  // Checking whether item is a Product or Product Family
  const isProduct = product.contentType === "product";
  const isProductFamily = product.contentType === "productFamily";

  // Checks if product is new, can be printable, is pro-planet, or is designed for recycling
  const isPrintable = product["isPrintable"] ? addYourBrandLabel : null;
  const isEmbossable = product["isEmbossable"] ? addYourBrandLabel : null;
  const isNew = product["isNewProduct"] ? newProductLabel : null;
  const isProPlanet = product["isProPlanet"];

  // Setting product details based on content type
  const imageUrl = isProduct ? product["catalogImage"] : product.promoImages?.[0];
  const productName = isProduct ? product["childProductName"] : product.promoTitle;
  const productSku = product["productCode"] || null;
  const productDescription = isProduct ? product["productDescription"] : product.promoDescription;

  // Handles Navigation Click
  const handleViewProductsOrDetailClick = () => {
    if (isProduct) {
      return "_self";
    } else if (isProductFamily) {
      return "_blank";
    }
  };

  // Getting Key Features
  const keyFeaturesArray = (() => {
    let keyFeatures = [];
    if (product.specialFeaturesCatalogFilter) {
      keyFeatures = product.specialFeaturesCatalogFilter;
    }
    // if only one feature, it is sometimes getting set to a string. Change to a single item array.
    if (typeof keyFeatures === "string") keyFeatures = [keyFeatures].flat();

    return keyFeatures.filter((item) => item !== null);
  })();

  // View More toggle hook for key features
  const { visibleItems: visibleKeyFeatures, isExpanded: keyFeaturesExpanded, needsExpansion: needsKeyFeaturesExpansion, toggle: toggleKeyFeatures } = useViewMoreToggle(
    keyFeaturesArray,
    NUMFEATURESDISPLAYED
  );

  // Getting Material Icons
  const materialIconsOutput = () => {
    const materialIcons =
      product.materialIcons && product.materialIcons.length > 0 ? product.materialIcons : [];

    return materialIcons
      .filter((item) => item !== null)
      .map((item, index) => {
        return (
          <li key={index} className="product-card__material-icons-list-item">
            <span className={`product-card__material-icon dart${item}`} aria-label={item} title={item} role="img"></span>
          </li>
        );
      });
  };

  // Getting Product Features
  const productFeaturesOutput = () => {
    const productFeatures =
      product.productFeatures && product.productFeatures.length > 0 ? product.productFeatures : [];

    return productFeatures
      .filter((item) => item !== null && item !== "")
      .map((item, index) => {
        return (
          <li key={index} className="product-card__product-sustainable-feature">
            <span className="product-card__button-icon darticon-checkmark checkmark"></span>
            {item}
          </li>
        );
      });
  };

  // Getting Sustainable Features
  const sustainableFeaturesOutput = () => {
    const features = getSustainableFeaturesList(product, sustainableLabels);
    
    return features.map((feature, index) => {
      const iconClass = feature.isProPlanet && feature.label === sustainableLabels.proPlanetSustainableLabel
        ? "darticon-proPlanet_Leaf"
        : "darticon-checkmark checkmark";
      
      return (
        <li key={index} className='product-card__product-sustainable-feature'>
          <span className={`product-card__button-icon ${iconClass}`}></span>
          {feature.label}
        </li>
      );
    });
  };

  const listOutput = isProduct ? sustainableFeaturesOutput() : productFeaturesOutput();

  const sustainableContainer = () => {
    const content = (
      <Desktop>
        <div className={`product-card__product-sustainable-container ${displayMode}`}>
          {/* List Header */}
          {listOutput.length > 0 && (
            <p className="product-card__product-body feature-label">
              {isProduct ? sustainableFeaturesLabel : productFeaturesLabel}:
            </p>
          )}
          {/* List Output */}
          {listOutput.length > 0 && (
            <ul className="product-card__product-sustainable-features">
              {listOutput}
            </ul>
          )}
          {(displayMode === DISPLAYMODE.LIST) && (
            <>
              {ctaButtonContainer()}
            </>
          )}
        </div>
      </Desktop>
    );
    return content;
  };

  const keyFeaturesContainer = () => {
    const content = (
      isProduct && keyFeaturesArray.length > 0 && (
        <Desktop>
          {/* List Header */}
          <p className="product-card__product-body feature-label">
            {keyFeaturesLabel}
          </p>
          {/* Key Features List */}
          <ul className="product-card__key-features">
            {visibleKeyFeatures.map((item, index) => (
              <li key={index} className="product-card__key-feature">
                {item}
              </li>
            ))}
          </ul>
          {/* View More / View Less CTA */}
          {needsKeyFeaturesExpansion && (
            <button
              className="product-card__view-more-less-cta"
              onClick={toggleKeyFeatures}
              type="button"
              label={`${keyFeaturesExpanded ? viewLessLabel : viewMoreLabel} ${productSku}`}
              aria-label={`${keyFeaturesExpanded ? viewLessLabel : viewMoreLabel} ${productSku}`}
            >
              <p className="product-card__view-more-less-text">
                {keyFeaturesExpanded ? viewLessLabel : viewMoreLabel}
              </p>
              <span
                className={`product-card__button-icon ${
                  keyFeaturesExpanded ? "darticon-arrow-up" : "darticon-arrow-down"
                } down-icon`}
              ></span>
            </button>
          )}
        </Desktop>
      )
    );
    return content;
  };

  const ctaButtonContainer = () => {
    const content = (
      <div className={`product-card__product-cta-container ${displayMode}`}>
          {product.url && product.url.length > 0 && !isDiscontinued && (
            <a
              className={`product-card__view-details-cta btn ${displayMode}`}
              aria-label={isProduct ? viewDetailsLabel + ' ' + productName : mlViewAllProducts}
              href={product.url}
              target={handleViewProductsOrDetailClick()}
              onClick={onCtaClick}>
              {isProduct ? viewDetailsLabel : mlViewAllProducts}
            </a>
          )}
          {product.promoLink && (
              product.promoLink.internalLink || product.promoLink.externalLink) && (
              <CtaLink
                className="product-card__view-details-cta btn"
                link={product.promoLink} />
          )}
      </div>
    );
    return content;
  };

  const renderCardContent = (renderDetails = false) => (
    <div className="product-card__wrapper">
      {/* Add Button */}
      {isProduct && product["productCode"] && !isDiscontinued && (
        <div className="product-card__button">
          <AddProduct
            productCode={product["productCode"]}
            productName={productName}
            btnSize={isMobile ? "small" : "medium"}
            discontinuedProduct={isDiscontinued}
            addToListAnalytics={customAddProductAnalytics?.addToListAnalytics || {
              event: "listAdd",
              listType: "Product Listing Page",
              productName: page ? `${getPageTitle(page)}` : productName,
            }}
            removeFromListAnalytics={customAddProductAnalytics?.removeFromListAnalytics || {
              event: "removeFromList",
              listType: "Product Listing Page",
              productName: page ? `${getPageTitle(page)}` : productName,
            }}
          />
        </div>
      )}

      <div className={`product-card__product ${displayMode}`}>
        {/* Image, Product Collection & CTA Section */}
        <div className="product-card__product-image-container">
          {/* Product Image */}
          {imageUrl && imageUrl.length > 0 && (
            <div className="product-card__product-image-wrapper">
              {/* New & Add Your Brand Banners */}
              {isProduct && (isPrintable || isEmbossable || isNew || isProPlanet) && (
                <div className="product-card__banners">
                  {/* "New!" */}
                  {isNew && (
                    <p className="product-card__banner primary">
                      {window.globalProperties["newProductLabel"] || "New!"}
                    </p>
                  )}
                  {/* "ProPlanet" */}
                  {isProPlanet && (
                    <p className="product-card__banner icon" aria-label="pro planet">
                      <img src="https://dart.widen.net/content/vsjmstuwtr/svg/ProPlanet-Leaf-CheckmarkwStroke-Green.svg?u=hsm95h" alt="" />
                    </p>
                  )}
                  {/* "Add Your Brand" */}
                  {(isPrintable || isEmbossable) && (
                    <p className="product-card__banner secondary bottom">
                      {window.globalProperties["addYourBrandLabel"] || "Add Your Brand"}
                    </p>
                  )}
                </div>
              )}
              <Image
                className="product-card__product-image"
                src={imageUrl}
                alt={productName}
                {...(productImageWidth ? { width: productImageWidth } : {})}
                {...(productImageHeight ? { height: productImageHeight } : {})}
              />
              {/* Discontinued Product Banner */}
              {isDiscontinued && discontinuedLabel && (
                <p className="product-card__product-discontinued">
                  {discontinuedLabel}
                </p>
              )}
              {/* Product Collection Banner */}
              {isProductFamily && (
                <p className="product-card__product-collection">
                  {productCollectionLabel}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Product Information Section */}
        <div className="product-card__product-info">
          {/* Product Sku & Info - moved above product name */}
          {isProduct && productSku && (
            <p className="product-card__product-body sku">
              #{productSku}
              {product.variations && product.variations.length > 0 ? <Desktop> | {moreColorsPrintsAvailableLabel}</Desktop> : null}
            </p>
          )}
          {/* Product Name */}
          {productName && (
            <Title
              defaultLevel={HEADING_LEVELS.TWO}
              className={`product-card__product-name ${displayMode}`}
            >
              {productName}
            </Title>
          )}

          {/* Additional Info - for custom fields like temperature usage, item count, etc. */}
          {renderAdditionalInfo && renderAdditionalInfo()}

          {/* Product Description */}
          {productDescription && productDescription.length > 0 &&
          (displayMode !== DISPLAYMODE.GRIDCOMPACT || renderDetails) && (
            <Description
              className={`product-card__product-body ${
                isProduct ? "desc" : "promo-desc"
              }`}
              value={productDescription}
            />
          )}
          {/* Product Info */}
          {isProduct && product.variations && product.variations.length > 0 && (
            <Mobile>
              <p className="product-card__product-body info">
                {moreColorsPrintsAvailableLabel}
              </p>
            </Mobile>
          )}
          {/* Discontinued Message */}
          {isDiscontinued && discontinuedMessage && (
            <Desktop>
              <Description
                className="product-card__product-body alert"
                value={discontinuedMessage}
              />
            </Desktop>
          )}
          {(displayMode === DISPLAYMODE.LIST) && (
            <>
              {keyFeaturesContainer()}
            </>
          )}
          {/* Material Icons */}
          {isProductFamily && materialIconsOutput().length >= 1 && (displayMode !== DISPLAYMODE.GRIDCOMPACT || renderDetails) && (
            <div className="product-card__material-icons-container">
              <ul className="product-card__material-icon-list">
                {materialIconsOutput()}
              </ul>
            </div>
          )}
        </div>

        {/* Key Features / Product & Sustainable Features / CTA Button : Sections are hidden on compact view */}
        <Mobile>
          {ctaButtonContainer()}
        </Mobile>
        <Desktop>
          {renderDetails ? (
            <>
              {keyFeaturesContainer()}
              {sustainableContainer()}
              {ctaButtonContainer()}
            </>
          ) : displayMode === DISPLAYMODE.GRIDEXPANDED ? (
            <>
              {keyFeaturesContainer()}
              {sustainableContainer()}
              {ctaButtonContainer()}
            </>
          ) : displayMode === DISPLAYMODE.LIST ? (
            <>
              {sustainableContainer()}
            </>
          ) : null}
        </Desktop>
      </div>

      {/* Discontinued Alert Message - Mobile */}
      {isDiscontinued && discontinuedMessage && (
        <Mobile>
          <Description
            className="product-card__product-body alert"
            value={discontinuedMessage}
          />
        </Mobile>
      )}
    </div>
  );

  return (
    <SearchCard
      className="product-card"
      displayMode={displayMode}
      enableHoverModal={true}
      externalModalDiv={externalModalDiv}
      positionModal={positionModal}
      renderContent={renderCardContent}
    />
  );
}

export default ProductCard;
